"use strict";
import { jsx, jsxs } from "react/jsx-runtime";
import s from "underscore.string";
import React from "react";
import { Link } from "react-router";
import { FlashMessages } from "../components/flash_messages";
import Api from "../api/api";
import Auth from "../util/auth";
import ModuleDropdown from "../components/module_dropdown";
import AlertArea from "../components/alert_area";
import LoadQuickSearch from "./load_quick_search";
import ListFilterHelper from "./list_filter_helper";
import DashboardReturnLocation from "./ui/dashboard_return_location";
import ElmApp from "./elm-app";
import BeamerUpdates from "./beamer_updates";
import StaffBar from "../shared/staff_bar";
import Account from "../api/account";
import Reskin from "../helpers/reskin";
const isSandbox = () => {
  return Auth._currentUser && Auth._currentUser.account && !Auth._currentUser.account.demo && Auth._currentUser.account.sandbox || false;
};
const onScraperPage = (location) => {
  return location && location.pathname === "/to_be_scraped_loads";
};
function shouldHideNavbar(location) {
  if (!location || !location.pathname) return false;
  return location.pathname.match(/\/billing_requirements/) || location.pathname.match(/\/documents_viewer/) || location.pathname.match(/\/okta_login/);
}
function documentPageExists() {
  return "documentPage" in window;
}
function mainView(children) {
  return children;
}
function mainViewContainerClass() {
  const v3ReskinReactStyleOverridesEnabledClasses = [
    "v3__theme",
    "audit_v3_reskin__activate_container",
    "v3_reskin__react-style-overrides-enabled"
  ];
  if (Reskin.getVals().indexAudit) {
    return v3ReskinReactStyleOverridesEnabledClasses.join(" ");
  } else {
    return "";
  }
}
function customAlertMessage() {
  if (Auth._currentUser && Auth._currentUser.account && Auth._currentUser.account.settings && Auth._currentUser.account.settings.custom_alert_message) {
    const message = Auth._currentUser.account.settings.custom_alert_message;
    return /* @__PURE__ */ jsx("div", { className: "alert-box alert-box--caution centered sandbox-warning", children: message });
  }
}
function logoutLink() {
  if (Auth.isAuthenticated()) {
    return /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(Link, { to: "/logout", children: "Log out" }) });
  }
}
function emailSearchLink() {
  if (Api.Account.currentSettings().email_search_account) {
    return /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(Link, { to: "/email_search", children: "Email Search" }) });
  }
}
function isHeldSubmissionPageEnabled() {
  const holdDays = Api.Account.currentSettings().ltl_hold_edi_import_days;
  return holdDays && holdDays > 0;
}
function heldSubmissionsLink() {
  if (isHeldSubmissionPageEnabled()) {
    return /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(Link, { to: "/held_submissions", children: "Held Submissions" }) });
  }
}
function hasSettingsRole() {
  return Auth.getCurrentUser() && !!Auth.getCurrentUser().settings_role;
}
function isDashboardReturnAccount() {
  return !!Account.isType("factor_account");
}
function switchUser(userId) {
  const current_user = Auth.getCurrentUser();
  if (current_user.id != userId) {
    Auth.switchUser(userId);
    return true;
  }
  return false;
}
function switchUserFromSelect(event) {
  switchUser(parseInt(event.target.value));
}
function groupUserOptions(group_user_info, current_user_id) {
  return group_user_info.map((row) => {
    return /* @__PURE__ */ jsx("option", { value: row["user_id"], children: row["account_name"] }, "group_user_" + row["user_id"]);
  });
}
function groupUsers() {
  if (Auth.isAuthenticated()) {
    const user = Auth.getCurrentUser();
    if (user.group_user_info && user.group_user_info.length > 0) {
      return /* @__PURE__ */ jsx("li", { className: "group-users-li", children: /* @__PURE__ */ jsx(
        "select",
        {
          className: "group-users-select",
          onChange: switchUserFromSelect,
          defaultValue: String(user.id),
          children: groupUserOptions(user.group_user_info, user.id)
        }
      ) });
    }
  }
}
function account() {
  return Auth._currentUser && Auth._currentUser.account;
}
function isPayablesAccount() {
  const currentAccount = account();
  return currentAccount && currentAccount.payables_account;
}
function isIndexAuditSplitAccount() {
  const currentAccount = account();
  return currentAccount && currentAccount.index_audit_split_enabled;
}
function dashboardModeParams() {
  const dashboardDestination = localStorage.getItem("dashboardDestination");
  if (!isIndexAuditSplitAccount() || !dashboardDestination) {
    return {};
  }
  return {
    dashboardMode: dashboardDestination === "/dashboard/ready-to-audit" ? "audit" : "index"
  };
}
function dashboardLinkLocation() {
  if (isIndexAuditSplitAccount()) {
    const localStorageValue = localStorage.getItem("dashboardDestination");
    if (localStorageValue === "/dashboard/new" || localStorageValue === "/dashboard/ready-to-audit") {
      return localStorageValue;
    }
  }
  return Account.matchAccountType({
    broker: () => isPayablesAccount() ? "/dashboard" : "/to_be_scraped_loads",
    factor: () => "/dashboard",
    forwarder: () => "/dashboard"
  });
}
function dashboardQueuePath(route, dashboardMode) {
  const path = route || dashboardLinkLocation();
  return isDashboardReturnAccount() ? DashboardReturnLocation.queuePathFor(path, dashboardMode || "") : path;
}
function dashboardLinkTo(route, extraParams) {
  const params = extraParams || dashboardModeParams();
  const path = dashboardQueuePath(route, params.dashboardMode);
  return isDashboardReturnAccount() ? DashboardReturnLocation.appendTo(path, params) : DashboardReturnLocation.withParams(path, params);
}
function dashboardLinkUrl() {
  return dashboardLinkTo();
}
function headerDropdownPages() {
  const currentAccount = account();
  return {
    payables: !!(currentAccount && (currentAccount.payables_account || currentAccount.factor_account)),
    noaManagement: !!(currentAccount && currentAccount.settings.noa_management_enabled),
    receivables: !!(currentAccount && currentAccount.receivables_account && Auth._currentUser.receivables),
    insights: !!(currentAccount && // broker_account is a Ruby-side alias for payables_account? and is not
    // serialized, so this read is always undefined.
    (currentAccount["broker_account"] || currentAccount.settings.insights_dashboard_enabled) && Auth._currentUser.insights_dashboard_access),
    ltlDocRetrieval: !!(currentAccount && currentAccount.scraper_account),
    customerRep: !!(currentAccount && currentAccount.customer_representative_view_enabled && Auth._currentUser.customer_representative)
  };
}
function headerRightPages() {
  if (!Auth.isAuthenticated()) {
    return [];
  }
  const isIndexer = !!Auth.getCurrentUser().indexer;
  const isAuditor = !!Auth.getCurrentUser().auditor;
  const isAdmin = !!Auth.getCurrentUser().admin;
  const indexDashboardUrl = dashboardLinkTo(
    isPayablesAccount() ? "/app/dashboard/new" : "/app/dashboard/indexing",
    { dashboardMode: "index" }
  );
  const auditDashboardUrl = dashboardLinkTo(
    isPayablesAccount() ? "/app/dashboard/ready-to-audit" : "/app/dashboard/new",
    { dashboardMode: "audit" }
  );
  const indexQueuesLink = {
    title: "Index queues",
    url: indexDashboardUrl,
    isVisible: true,
    possibleLocationRegexes: [
      "^/app/dashboard$",
      "^/app/dashboard/new$",
      "^/app/dashboard.*dashboardMode=index",
      "^/app/index-document-batches"
    ]
  };
  const auditQueuesLink = {
    title: "Audit queues",
    url: auditDashboardUrl,
    isVisible: true,
    possibleLocationRegexes: [
      "^/app/dashboard.*dashboardMode=audit",
      "^/app/loads/"
    ]
  };
  let indexAuditLinks;
  if (isIndexAuditSplitAccount()) {
    if (isIndexer && isAuditor || isAdmin) {
      indexAuditLinks = [indexQueuesLink, auditQueuesLink];
    } else if (isIndexer) {
      indexQueuesLink.title = "Queues";
      indexQueuesLink.possibleLocationRegexes.push("^/app/dashboard");
      indexAuditLinks = [indexQueuesLink];
    } else if (isAuditor) {
      auditQueuesLink.title = "Queues";
      auditQueuesLink.possibleLocationRegexes.push("^/app/dashboard");
      indexAuditLinks = [auditQueuesLink];
    } else {
      indexAuditLinks = [];
    }
  } else {
    indexQueuesLink.title = "Queues";
    indexQueuesLink.possibleLocationRegexes.push("^/app/dashboard");
    indexQueuesLink.url = dashboardLinkUrl();
    indexAuditLinks = [indexQueuesLink];
  }
  const links = indexAuditLinks.concat([
    {
      title: "Email search",
      url: "/app/email_search",
      isVisible: Api.Account.currentSettings().email_search_account,
      possibleLocationRegexes: ["^/app/email_search", "^/app/email/"]
    },
    {
      title: "Activity",
      url: "/app/activity",
      isVisible: true,
      possibleLocationRegexes: ["^/app/activity"]
    },
    {
      title: "Held submissions",
      url: "/app/held_submissions",
      isVisible: isHeldSubmissionPageEnabled(),
      possibleLocationRegexes: ["^/app/held_submissions"]
    }
  ]);
  return links.filter((link) => link.isVisible).map((link) => {
    return {
      title: link.title,
      url: link.url,
      possibleLocationRegexes: link.possibleLocationRegexes
    };
  });
}
function indexDashboardLink(label) {
  const route = isPayablesAccount() ? "/dashboard/new" : "/dashboard/indexing";
  return /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(
    Link,
    {
      to: dashboardLinkTo(route, { dashboardMode: "index" }),
      onClick: () => isIndexAuditSplitAccount() && localStorage.setItem("dashboardDestination", route),
      children: label
    }
  ) });
}
function auditDashboardLink(label) {
  const route = isPayablesAccount() ? "/dashboard/ready-to-audit" : "/dashboard/new";
  return /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(
    Link,
    {
      to: dashboardLinkTo(route, { dashboardMode: "audit" }),
      onClick: () => isIndexAuditSplitAccount() && localStorage.setItem("dashboardDestination", route),
      children: label
    }
  ) });
}
function indexAuditDropdownMenu() {
  return /* @__PURE__ */ jsx("ul", { className: "left", id: "module-dropdown", children: /* @__PURE__ */ jsxs("li", { className: "has-dropdown not-click", children: [
    /* @__PURE__ */ jsx("a", { children: "Dashboard" }),
    /* @__PURE__ */ jsxs("ul", { className: "dropdown", children: [
      indexDashboardLink("Index"),
      auditDashboardLink("Audit")
    ] })
  ] }) });
}
function headerFlagsForElm(state) {
  let accounts = [];
  if (Auth.isAuthenticated()) {
    const user = Auth.getCurrentUser();
    if (user.group_user_info && user.group_user_info.length > 0) {
      accounts = user.group_user_info.map((group_user) => {
        return {
          id: group_user["user_id"],
          name: group_user["account_name"],
          isCurrent: group_user["user_id"] === user.id
        };
      });
    }
  }
  return {
    reskinVals: Reskin.getVals(),
    showNavBar: !state.hideNavbar,
    reskinUserToggle: Reskin.getReskinUserToggle(),
    dashboardLinkLocation: dashboardLinkUrl(),
    headerDropdownPages: headerDropdownPages(),
    headerRightPages: headerRightPages(),
    hasSettingsRole: !!hasSettingsRole(),
    useSubmissionWorkflow: !!Account.useSubmissionWorkflow(),
    isFactor: !!Account.hasRole("Factor"),
    accounts
  };
}
function sandboxMessage(props) {
  if (isSandbox() && !onScraperPage(props.location)) {
    const loadEmailSandboxRecipients = Api.Account.loadEmailSandboxRecipients();
    let message = "This account is currently in sandbox mode. No invoices will be sent to external systems.";
    if (loadEmailSandboxRecipients) {
      message += " All emails will go to " + s.toSentenceSerial(loadEmailSandboxRecipients, ", ") + ".";
    }
    return /* @__PURE__ */ jsx("div", { className: "alert-box warning centered sandbox-warning", children: message });
  }
}
function dashboardParams(props) {
  if (props.location.pathname === "/dashboard/replies") {
    return ListFilterHelper.queryWithFilters(
      Auth._currentUser.settings.preferred_filter || { All: "true" }
    );
  }
  if (!isDashboardReturnAccount() && (props.location.pathname === "/dashboard" || props.location.pathname === "/dashboard/new")) {
    return props.location.query;
  }
}
function navbarDashboardLinkTo(props) {
  return dashboardParams(props) ? dashboardQueuePath(null, dashboardModeParams().dashboardMode) : dashboardLinkTo();
}
function dashboardLinkOrDropdown(props) {
  const isIndexer = !!Auth.getCurrentUser().indexer;
  const isAuditor = !!Auth.getCurrentUser().auditor;
  const isAdmin = !!Auth.getCurrentUser().admin;
  if (isIndexAuditSplitAccount()) {
    if (isIndexer && isAuditor || isAdmin) {
      return indexAuditDropdownMenu();
    } else if (isIndexer) {
      return indexDashboardLink("Dashboard");
    } else if (isAuditor) {
      return auditDashboardLink("Dashboard");
    } else {
      return dashboardLink(props);
    }
  } else {
    return dashboardLink(props);
  }
}
function dashboardLink(props) {
  return /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(Link, { to: navbarDashboardLinkTo(props), query: dashboardParams(props), children: "Dashboard" }) });
}
const App = React.createClass({
  getInitialState: function() {
    const loggedIn = Auth.isAuthenticated();
    return {
      loggedIn,
      reskinToggleValFromStaffBar: Reskin.getSavedToggle(),
      reskinToggleValFromNavBar: Reskin.getReskinUserToggle(),
      hideNavbar: shouldHideNavbar(this.props.location)
    };
  },
  updateAuth: function(loggedIn) {
    this.setState({
      loggedIn
    });
    if (this.refs.elmApp) {
      this.refs.elmApp.updateHeaderFlags(headerFlagsForElm(this.state));
    }
  },
  componentWillMount: function() {
    Auth.onChange = this.updateAuth;
    Auth.currentUser().catch((error) => {
      if (error.response.status !== 401) throw error;
    });
    if (isIndexAuditSplitAccount()) {
      if (this.props.location.pathname.includes("/dashboard") && this.props.location.query["dashboardMode"]) {
        let dashboardDestination;
        if (this.props.location.query["dashboardMode"] === "audit" && isPayablesAccount()) {
          dashboardDestination = "/dashboard/ready-to-audit";
        } else if (this.props.location.query["dashboardMode"] === "index" && !isPayablesAccount()) {
          dashboardDestination = "/dashboard/indexing";
        } else {
          dashboardDestination = "/dashboard/new";
        }
        localStorage.setItem("dashboardDestination", dashboardDestination);
      }
    }
  },
  componentWillReceiveProps: function(nextProps) {
    const hideNavbar = shouldHideNavbar(nextProps.location);
    this.setState({ hideNavbar });
  },
  rightTopBarLinks: function() {
    if (Auth.isAuthenticated()) {
      return /* @__PURE__ */ jsxs("ul", { className: "right", children: [
        /* @__PURE__ */ jsx("li", { id: "load-quick-search", className: "has_form", children: /* @__PURE__ */ jsx(LoadQuickSearch, { location: this.props.location }) }),
        dashboardLinkOrDropdown(this.props),
        heldSubmissionsLink(),
        /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(Link, { to: "/activity", children: "Activity" }) }),
        emailSearchLink(),
        this.settingsLink(),
        /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(Link, { to: "/help", children: "Help" }) }),
        /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(BeamerUpdates, {}) }),
        groupUsers(),
        logoutLink()
      ] });
    }
  },
  render: function() {
    if (documentPageExists()) {
      return /* @__PURE__ */ jsxs("div", { id: "mainViewContainer", "data-component": "App", children: [
        sandboxMessage(this.props),
        mainView(this.props.children)
      ] });
    } else {
      const elmHeaderFlags = headerFlagsForElm(this.state);
      const hideLegacyNavbar = this.state.hideNavbar || Reskin.getVals().topLevel;
      const legacyNavbar = hideLegacyNavbar ? null : this.navbar();
      return /* @__PURE__ */ jsxs("div", { className: "react-wrapper", "data-component": "App", children: [
        /* @__PURE__ */ jsx(
          StaffBar,
          {
            ref: "staffBar",
            currentUser: Auth._currentUser,
            loggedIn: this.state.loggedIn,
            toggleReskin: (evt) => this.toggleReskinFromStaffBar(evt.target.value),
            reskinToggleVal: this.state.reskinToggleValFromStaffBar
          }
        ),
        /* @__PURE__ */ jsx("div", { className: "contain-to-grid", children: legacyNavbar }),
        /* @__PURE__ */ jsx(AlertArea, {}),
        /* @__PURE__ */ jsxs("div", { id: "container", children: [
          /* @__PURE__ */ jsx("div", { id: "flash-messages", children: /* @__PURE__ */ jsx(FlashMessages, {}) }),
          /* @__PURE__ */ jsxs("div", { children: [
            customAlertMessage(),
            sandboxMessage(this.props)
          ] }),
          /* @__PURE__ */ jsx("div", { id: "elm-container", children: /* @__PURE__ */ jsx(
            ElmApp,
            {
              ref: "elmApp",
              location: this.props.location,
              headerFlags: elmHeaderFlags,
              switchUserCallback: switchUser,
              toggleReskin: this.toggleReskinFromNavBar,
              dashboardFiltersChanged: this.refreshDashboardHeaderLinks,
              urlChanged: this.refreshStaffBar
            }
          ) }),
          /* @__PURE__ */ jsx("div", { id: "mainViewContainer", className: mainViewContainerClass(), children: mainView(this.props.children) })
        ] })
      ] });
    }
  },
  navbar: function() {
    if (!this.state.hideNavbar) {
      return /* @__PURE__ */ jsxs("nav", { className: "top-bar main-nav", "data-topbar": true, role: "navigation", children: [
        /* @__PURE__ */ jsx("ul", { className: "title-area show-for-large-up", children: /* @__PURE__ */ jsx("li", { className: "name", children: /* @__PURE__ */ jsx("h1", { children: /* @__PURE__ */ jsx(
          Link,
          {
            to: navbarDashboardLinkTo(this.props),
            query: dashboardParams(this.props),
            children: /* @__PURE__ */ jsx(
              "img",
              {
                style: {
                  borderRadius: "4px",
                  background: "rgba(255, 255, 255, 0.40)",
                  padding: "4px"
                },
                src: "/assets/TriumphLogo.svg",
                className: "hubtran-logo"
              }
            )
          }
        ) }) }) }),
        /* @__PURE__ */ jsxs("section", { className: "top-bar-section", children: [
          /* @__PURE__ */ jsx(ModuleDropdown, { location: this.props.location }),
          this.rightTopBarLinks()
        ] })
      ] });
    }
  },
  settingsLink() {
    const userHasSettingsRole = hasSettingsRole();
    const reskinToggleVisible = Reskin.getReskinUserToggle() != null;
    if (reskinToggleVisible) {
      const switchStr = "Beginning May 26, 2026, Triumph Audit will no longer support our previous experience and layout.";
      return /* @__PURE__ */ jsx("ul", { id: "module-dropdown", children: /* @__PURE__ */ jsxs("li", { className: "has-dropdown not-click", children: [
        /* @__PURE__ */ jsx("a", { children: "Settings" }),
        /* @__PURE__ */ jsxs("ul", { className: "dropdown", style: { right: "auto" }, children: [
          userHasSettingsRole && /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(Link, { to: "/settings", children: "Account Settings" }) }),
          /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsxs(
            "a",
            {
              style: { padding: "18px", lineHeight: "150%" },
              onClick: () => this.toggleReskinFromNavBar(
                !this.state.reskinToggleValFromNavBar
              ),
              "data-fullstory-reskin-toggle": "enable",
              children: [
                /* @__PURE__ */ jsxs(
                  "div",
                  {
                    style: {
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "12px"
                    },
                    children: [
                      /* @__PURE__ */ jsx("div", { children: "Layout Preferences" }),
                      /* @__PURE__ */ jsx(
                        "div",
                        {
                          style: {
                            borderRadius: "16px",
                            color: "rgb(0, 124, 143)",
                            fontSize: "12px",
                            lineHeight: "12px",
                            border: "1px solid rgb(186, 239, 247)",
                            backgroundColor: "rgb(250, 250, 250)",
                            padding: "4px 8px 2px"
                          },
                          children: "New"
                        }
                      )
                    ]
                  }
                ),
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    style: {
                      whiteSpace: "break-spaces",
                      fontSize: "12px",
                      minWidth: "220px"
                    },
                    children: switchStr
                  }
                ),
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    style: {
                      color: "rgb(152, 229, 240)",
                      fontSize: "12px"
                    },
                    children: "Switch Layout"
                  }
                )
              ]
            }
          ) })
        ] })
      ] }) });
    } else if (userHasSettingsRole) {
      return /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(Link, { to: "/settings", children: "Settings" }) });
    }
  },
  // Elm holds its header flags in its own model, so a filter change has to be
  // pushed back over the port for the Elm header's Dashboard links to pick up
  // the new filters. Only re-send when a link URL actually changed, so a burst
  // of filter edits doesn't churn the Elm header.
  refreshDashboardHeaderLinks() {
    if (!this.refs.elmApp) return;
    const flags = headerFlagsForElm(this.state);
    const linkUrls = JSON.stringify([
      flags.dashboardLinkLocation,
      flags.headerRightPages.map((page) => page.url)
    ]);
    if (linkUrls === this.lastDashboardHeaderLinkUrls) return;
    this.lastDashboardHeaderLinkUrls = linkUrls;
    this.refs.elmApp.updateHeaderFlags(flags);
  },
  refreshStaffBar() {
    var _a;
    (_a = this.refs.staffBar) == null ? void 0 : _a.refreshLocation();
  },
  toggleReskinFromStaffBar(val) {
    this.setState({ reskinToggleValFromStaffBar: val });
    Reskin.saveToggle(val);
    this.refs.elmApp.updateHeaderFlags(headerFlagsForElm(this.state));
  },
  toggleReskinFromNavBar(enabled) {
    const newVal = enabled;
    Reskin.saveToggleUser(newVal);
    this.setState({ reskinToggleValFromNavBar: newVal });
    this.refs.elmApp.updateHeaderFlags(headerFlagsForElm(this.state));
  }
});
export default App;
